import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { useUserData } from '../contexts/UserDataContext';
import { useAuth } from '../contexts/AuthContext';
import ItemCard from './ItemCard';

const difficulties = ['low', 'medium', 'high'];

export default function MemorizeWordsPage({ selectedLanguage, userId }: { selectedLanguage?: string, userId: string }) {
    const { userData, refreshUserData } = useUserData();
    const { currentUser } = useAuth();
    const actualUserId = userId || currentUser?.uid || '';
    const actualSelectedLanguage = selectedLanguage || userData?.selectedLanguage || 'en';
    const [selectedDifficulty, setSelectedDifficulty] = useState(difficulties[0]);
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
    const [userCount, setUserCount] = useState<number>(0);
    const batchSize = 3;
    const { addFavoriteWord, getCompletedWords, addCompletedWord } = useUserData();
    const [completedIds, setCompletedIds] = useState<string[]>([]);
    // Add state to track which words are revealed
    const [revealed, setRevealed] = useState<{ [key: string]: boolean }>({});
    // Add state for user input and feedback
    const [userInputs, setUserInputs] = useState<{ [key: string]: string }>({});
    const [inputStatus, setInputStatus] = useState<{ [key: string]: 'idle' | 'correct' | 'incorrect' }>({});
    const [favoriteIds, setFavoriteIds] = useState<string[]>([]);

    useEffect(() => {
        if (!actualUserId) return;
        console.log('Fetching memorized words for user:', actualUserId, 'difficulty:', selectedDifficulty);
        setLoading(true);
        api.getMemorizedWords(actualUserId, selectedDifficulty)
            .then(res => {
                // Defensive log for _id presence
                if (Array.isArray(res)) {
                    res.forEach((item, idx) => {
                        if (!item._id) {
                            console.warn('MemorizeWordsPage: Missing _id for item at index', idx, item);
                        }
                    });
                }
                setItems(res || []);
                setCurrentBatchIndex(0);
            })
            .catch(error => {
                console.error('Error fetching memorized words:', error);
            })
            .finally(() => setLoading(false));

        // Remove the unnecessary count API call
        // api.getMemorizeWordsCount(actualUserId, selectedDifficulty)
        //     .then(count => {
        //         setUserCount(count);
        //     })
        //     .catch(err => {
        //         console.error('Error fetching memorize words count:', err);
        //         setUserCount(0);
        //     });
    }, [selectedDifficulty, actualUserId]);

    useEffect(() => {
        getCompletedWords(selectedDifficulty).then(res => {
            setCompletedIds(res.map(w => w._id || ''));
        });
        // Remove the duplicate favorite words API call - this should be cached in context
        // if (actualUserId) {
        //     api.getFavoriteWords(actualUserId, selectedDifficulty)
        //         .then(favs => {
        //             setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
        //         });
        // }
    }, [selectedDifficulty, actualUserId]);

    const isWordFavorite = (word: string) => {
        return userData?.favoriteWords?.some(fav => fav.en === word);
    };

    const isFavorite = (item: any) => favoriteIds.includes(`word:${item.masterId}`);

    const handleAction = async (idx: number, status: 'memorized' | 'memorizeAgain' | 'favorite') => {
        if (!actualUserId || !items[idx]) return;
        if (status === 'favorite') {
            if (isFavorite(items[idx])) return;
            // Optimistically update favoriteIds
            setFavoriteIds(prev => [...prev, `word:${items[idx].masterId}`]);
            await addFavoriteWord(
                `word:${items[idx].masterId}`,
                selectedDifficulty
            );
            // Refresh favoriteIds after favoriting (in case of backend changes)
            api.getFavoriteWords(actualUserId, selectedDifficulty)
                .then(favs => {
                    setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
                });
            return;
        }
        if (status === 'memorized') {
            if (items[idx]._id) {
                await addCompletedWord(
                    `word:${items[idx].masterId}`,
                    items[idx].difficulty || 'low'
                );
                await api.memorizeWord(
                    actualUserId,
                    `word:${items[idx].masterId}`,
                    items[idx].difficulty || 'low',
                    'done'
                );
            }
        } else if (status === 'memorizeAgain') {
            if (items[idx]._id) {
                await api.memorizeWord(
                    actualUserId,
                    `word:${items[idx].masterId}`,
                    items[idx].difficulty || 'low',
                    'memorizeAgain'
                );
            }
        }
        const newItems = items.filter((_, i) => i !== idx);
        setItems(newItems);
        const batchStart = currentBatchIndex * batchSize;
        if (batchStart >= newItems.length && currentBatchIndex > 0) {
            setCurrentBatchIndex(currentBatchIndex - 1);
        }
    };

    const filteredItems = items.filter(item => !completedIds.includes(item._id));

    const batchStart = currentBatchIndex * batchSize;
    const batchEnd = batchStart + batchSize;
    const currentBatch = filteredItems.slice(batchStart, batchEnd);

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-4xl font-extrabold tracking-tight drop-shadow-lg mb-6 text-center">
                    <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Memorize Words</span>
                </h1>
            </div>
            <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                    {/* Removed Selected Language text */}
                    <div className="flex flex-wrap gap-2 justify-center w-full">
                        {difficulties.map((difficulty, idx) => (
                            <button
                                key={difficulty}
                                className={`px-6 py-2 rounded-full font-semibold text-base transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-300 ${selectedDifficulty === difficulty ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white scale-105' : 'bg-blue-100 text-blue-800 hover:bg-gradient-to-r hover:from-blue-200 hover:via-purple-100 hover:to-pink-100 hover:text-blue-900'}`}
                                onClick={() => setSelectedDifficulty(difficulty)}
                            >
                                {idx === 0 ? 'Beginner Level' : idx === 1 ? 'Intermediate Level' : 'Advanced Level'}
                            </button>
                        ))}
                    </div>
                </div>
                {/* Count and navigation */}
                <div className="text-center mb-4">
                    <p className="text-gray-600">
                        Showing {items.length === 0 ? 0 : batchStart + 1} - {Math.min(batchEnd, items.length)} of {items.length} words to memorize (Total: {items.length})
                    </p>
                </div>
                {loading ? (
                    <div className="text-center text-lg text-blue-500 font-semibold py-12">Loading...</div>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-6 justify-center items-stretch w-full">
                            {currentBatch.map((item, idx) => {
                                const favoriteButton = (
                                    <button
                                        className={`px-3 py-1 rounded bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs font-semibold shadow hover:from-yellow-500 hover:to-yellow-700 transition ${isFavorite(item) ? 'bg-yellow-500 text-white cursor-not-allowed opacity-60' : ''}`}
                                        onClick={() => handleAction(idx, 'favorite')}
                                        disabled={isFavorite(item)}
                                        title={isFavorite(item) ? 'Already favorited' : 'Favorite'}
                                    >
                                        <span>⭐</span> Favorite
                                    </button>
                                );
                                return (
                                    <div key={item._id || idx} className="mb-4 w-full max-w-xl bg-white rounded-xl shadow p-4 flex flex-col gap-2">
                                        {/* Selected language on top */}
                                        <div className="text-xl font-bold text-gray-900 mb-1">
                                            {item[actualSelectedLanguage] || <span className='italic text-gray-400'>Translation not found</span>}
                                        </div>
                                        {/* English word hidden by default, with view button */}
                                        {revealed[item._id] ? (
                                            <div className="text-base text-gray-700 mt-1">{item.en}</div>
                                        ) : (
                                            <div className="flex items-center gap-2 mt-1">
                                                <div className="flex-1 h-10 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-300 rounded-xl flex items-center justify-center italic shadow-inner border border-gray-300">
                                                    <span className="mr-2 text-gray-400">
                                                        <svg xmlns='http://www.w3.org/2000/svg' className='inline w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 17a2 2 0 100-4 2 2 0 000 4zm6-2V9a6 6 0 10-12 0v6a2 2 0 002 2h8a2 2 0 002-2z' /></svg>
                                                    </span>
                                                    <span className="text-gray-400">Hidden</span>
                                                </div>
                                                <button
                                                    className="ml-2 px-4 py-1 rounded-xl bg-gradient-to-r from-blue-400 to-blue-600 text-white text-xs font-bold shadow hover:from-blue-500 hover:to-blue-700 transition flex items-center gap-1"
                                                    onClick={() => setRevealed(r => ({ ...r, [item._id]: true }))}
                                                >
                                                    <svg xmlns='http://www.w3.org/2000/svg' className='inline w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' /><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' /></svg>
                                                    View
                                                </button>
                                            </div>
                                        )}
                                        {/* Stylish input box for user to type English word */}
                                        <form
                                            className="mt-2 flex flex-col gap-2"
                                            onSubmit={e => {
                                                e.preventDefault();
                                                const normalize = (str: string) =>
                                                    (str || '')
                                                        .toLowerCase()
                                                        .replace(/\s+/g, ' ')
                                                        .replace(/[^\w\s]/g, '')
                                                        .trim();
                                                const userInput = normalize(userInputs[item._id]);
                                                const correct = normalize(item.en);
                                                setInputStatus(s => ({ ...s, [item._id]: userInput === correct ? 'correct' : 'incorrect' }));
                                            }}
                                        >
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    className={`w-full px-4 py-2 rounded-xl border text-base shadow focus:outline-none transition-all font-semibold tracking-wide bg-gradient-to-r from-gray-50 to-gray-100 placeholder-gray-400 ${inputStatus[item._id] === 'correct' ? 'bg-green-100 border-green-400' : inputStatus[item._id] === 'incorrect' ? 'bg-red-100 border-red-400' : 'border-gray-300'}`}
                                                    placeholder="Type the English word..."
                                                    value={userInputs[item._id] || ''}
                                                    onChange={e => {
                                                        setUserInputs(u => ({ ...u, [item._id]: e.target.value }));
                                                        setInputStatus(s => ({ ...s, [item._id]: 'idle' }));
                                                    }}
                                                />
                                                <button
                                                    type="submit"
                                                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-green-400 to-green-600 text-white text-sm font-bold shadow hover:from-green-500 hover:to-green-700 transition flex items-center gap-1"
                                                >
                                                    <svg xmlns='http://www.w3.org/2000/svg' className='inline w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' /></svg>
                                                    Check
                                                </button>
                                            </div>
                                        </form>
                                        {/* Action buttons and rest of card */}
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            <button
                                                className="flex items-center gap-2 px-6 py-2 rounded-full bg-gradient-to-r from-green-400 to-green-600 text-white font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-300"
                                                onClick={async () => {
                                                    if (item._id) {
                                                        await addCompletedWord(
                                                            `word:${item.masterId}`,
                                                            item.difficulty || 'low'
                                                        );
                                                        await api.memorizeWord(
                                                            actualUserId,
                                                            `word:${item.masterId}`,
                                                            item.difficulty || 'low',
                                                            'done'
                                                        );
                                                        setItems(prev => prev.filter(i => i._id !== item._id));
                                                    }
                                                }}
                                            >
                                                <span>✓</span> Done
                                            </button>
                                            <button
                                                className="flex items-center gap-2 px-6 py-2 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-300"
                                                onClick={async () => {
                                                    if (actualUserId && item._id) {
                                                        await api.memorizeWord(actualUserId, `word:${item.masterId}`, item.difficulty || 'low', 'memorizeAgain');
                                                        setItems(items => items.filter(i => i._id !== item._id));
                                                    }
                                                }}
                                            >
                                                <span>🧠</span> Memorize Again
                                            </button>
                                            <button
                                                className={`flex items-center gap-2 px-6 py-2 rounded-full font-bold shadow-lg text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 ${isFavorite(item) ? 'bg-yellow-500 text-white cursor-not-allowed opacity-60' : 'bg-gradient-to-r from-yellow-200 to-yellow-400 text-yellow-800 hover:from-yellow-300 hover:to-yellow-500'}`}
                                                onClick={async () => {
                                                    if (isFavorite(item)) return;
                                                    setFavoriteIds(prev => [...prev, `word:${item.masterId}`]);
                                                    await addFavoriteWord(
                                                        `word:${item.masterId}`,
                                                        selectedDifficulty
                                                    );
                                                    api.getFavoriteWords(actualUserId, selectedDifficulty)
                                                        .then(favs => {
                                                            setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
                                                        });
                                                }}
                                                disabled={isFavorite(item)}
                                                title={isFavorite(item) ? 'Already favorited' : 'Favorite'}
                                            >
                                                <span className={isFavorite(item) ? 'text-yellow-100' : 'text-yellow-600'}>⭐</span> Favorite
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                            {filteredItems.length === 0 && (
                                <div className="flex-1 text-center text-gray-500 font-semibold py-12">No words to memorize in this group!</div>
                            )}
                        </div>
                        {/* Navigation buttons */}
                        {items.length > batchSize && (
                            <div className="flex justify-center gap-4 mt-6">
                                <button
                                    className="px-4 py-2 rounded-full bg-gray-200 text-gray-700 font-semibold disabled:opacity-50"
                                    onClick={() => setCurrentBatchIndex(i => Math.max(0, i - 1))}
                                    disabled={currentBatchIndex === 0}
                                >
                                    Previous
                                </button>
                                <button
                                    className="px-4 py-2 rounded-full bg-blue-500 text-white font-semibold disabled:opacity-50"
                                    onClick={() => setCurrentBatchIndex(i => (batchEnd < items.length ? i + 1 : i))}
                                    disabled={batchEnd >= items.length}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
} 